import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import LockIcon from '@mui/icons-material/Lock';
import InfoIcon from '@mui/icons-material/Info';

const theme = {
  primary: '#B43361',
  secondary: '#F3D4D4',
  background: '#FFFFFF',
  text: '#000000',
  lightGreen: '#E8F5E9',
};

const API_BASE_URL = 'https://bosscrowns-api-a228488a1e46.herokuapp.com/bosscrowns/trivia-sessions';

const CreateTriviaSession = () => {
  const navigate = useNavigate();
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [gameTemplates, setGameTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const isAdmin = true; // Replace with actual admin check

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await fetch('https://bosscrowns-api-a228488a1e46.herokuapp.com/bosscrowns/trivia');
        const data = await response.json();
        setGameTemplates(data);
      } catch (err) {
        console.error('Error fetching trivia templates:', err);
      }
    };
    fetchTemplates();

    const storedPassword = sessionStorage.getItem('adminPassword');
    if (storedPassword) {
      verifyPassword(storedPassword);
    } else {
      setPasswordModalOpen(true);
    }
  }, []);

  const verifyPassword = async (pwd) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwd }),
      });
      const data = await response.json();
      if (data.success) {
        sessionStorage.setItem('adminPassword', pwd);
        setPasswordModalOpen(false);
        setPasswordError(null);
        setIsAuthenticated(true);
      } else {
        sessionStorage.removeItem('adminPassword');
        setPasswordError(data.message || 'Invalid password');
      }
    } catch (error) {
      sessionStorage.removeItem('adminPassword');
      setPasswordError('Server error');
    } finally {
      setIsLoading(false);
    }
  };

  const createSession = async () => {
    if (!isAdmin || !isAuthenticated || !selectedTemplate) {
      alert('Please authenticate and select a game template');
      return;
    }
    try {
      setIsLoading(true);
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gameTemplateId: selectedTemplate }),
      });
      if (!response.ok) throw new Error('Failed to create trivia session');
      const { pin } = await response.json();
      console.log('Created trivia session with PIN:', pin);
      navigate(`/trivia/game-master/${pin}`);
    } catch (err) {
      alert(`Error creating trivia session: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="sm" sx={{ py: 5, bgcolor: theme.background }}>
        <Card sx={{ boxShadow: 3, bgcolor: theme.background }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
              <LockIcon sx={{ color: theme.primary, mr: 1 }} />
              <Typography variant="h6" sx={{ color: theme.text }}>
                Access Denied: Only admins can access this page.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 5, bgcolor: theme.background }}>
      <Dialog open={passwordModalOpen} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: theme.primary, color: theme.background, display: 'flex', alignItems: 'center' }}>
          <LockIcon sx={{ mr: 1 }} />
          Admin Authentication
        </DialogTitle>
        <DialogContent sx={{ bgcolor: theme.background, pt: 2 }}>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <Typography variant="body1" sx={{ color: theme.text, mb: 2 }}>
            Please enter the admin password to access trivia session creation.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            autoFocus
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: theme.primary,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.primary,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: theme.background }}>
          <Button
            onClick={() => navigate('/')}
            variant="outlined"
            disabled={isLoading}
            sx={{ color: theme.text, borderColor: theme.text }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => verifyPassword(password)}
            variant="contained"
            disabled={isLoading || !password}
            sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.secondary } }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {!passwordModalOpen && (
        <Card sx={{ boxShadow: 3, bgcolor: theme.background, borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <CasinoIcon sx={{ fontSize: 60, color: theme.primary, mb: 2 }} />
              <Typography variant="h4" sx={{ color: theme.text, mb: 2, fontWeight: 'bold' }}>
                Create a New Trivia Session
              </Typography>
              <Typography variant="body1" sx={{ color: theme.text, mb: 4, maxWidth: 600 }}>
                Start a new trivia game session as the Game Master. Select a game template and a unique PIN will be generated for players to join.
              </Typography>
              <FormControl fullWidth sx={{ mb: 3, maxWidth: 400 }}>
                <InputLabel sx={{ '&.Mui-focused': { color: theme.primary } }}>
                  Select Game Template
                </InputLabel>
                <Select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.text,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.primary,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.primary,
                    },
                  }}
                >
                  {gameTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.id} {template.isActive && '(Active)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  mb: 4,
                  bgcolor: theme.lightGreen,
                  borderRadius: 2,
                  width: '100%',
                  maxWidth: 600,
                }}
              >
                <Typography variant="h6" sx={{ color: theme.text, mb: 2, display: 'flex', alignItems: 'center' }}>
                  <InfoIcon sx={{ mr: 1, color: theme.primary }} />
                  How to Create a Trivia Session
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <InfoIcon sx={{ color: theme.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Select a trivia game template."
                      secondary="Choose from available templates to set the questions."
                      primaryTypographyProps={{ color: theme.text }}
                      secondaryTypographyProps={{ color: theme.text }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <InfoIcon sx={{ color: theme.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Click 'Create New Session'."
                      secondary="This will generate a unique PIN."
                      primaryTypographyProps={{ color: theme.text }}
                      secondaryTypographyProps={{ color: theme.text }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <InfoIcon sx={{ color: theme.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Share the PIN with players."
                      secondary="Players can join using this PIN."
                      primaryTypographyProps={{ color: theme.text }}
                      secondaryTypographyProps={{ color: theme.text }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <InfoIcon sx={{ color: theme.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Manage the session."
                      secondary="Advance questions and end the session from the Game Master dashboard."
                      primaryTypographyProps={{ color: theme.text }}
                      secondaryTypographyProps={{ color: theme.text }}
                    />
                  </ListItem>
                </List>
              </Paper>
              <Button
                variant="contained"
                onClick={createSession}
                size="large"
                startIcon={<CasinoIcon />}
                disabled={isLoading || !selectedTemplate}
                sx={{
                  bgcolor: theme.primary,
                  '&:hover': { bgcolor: theme.secondary },
                  padding: '12px 24px',
                  fontSize: '1.1rem',
                  borderRadius: 8,
                }}
              >
                Create New Session
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default CreateTriviaSession;